import { useState } from "react";
import { Leaf, User, Phone, ArrowRight, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { authorizeGreenEnergy } from "../api/greenEnergy";

const DISCOM_OPTIONS = [
  { value: "pgvcl", label: "PGVCL - Paschim Gujarat Vij Company Limited" },
  { value: "ugvcl", label: "UGVCL - Uttar Gujarat Vij Company Limited" },
  { value: "mgvcl", label: "MGVCL - Madhya Gujarat Vij Company Limited" },
  { value: "dgvcl", label: "DGVCL - Dakshin Gujarat Vij Company Limited" },
  { value: "jeg", label: "JEG - Gujarat Energy Transmission Corporation" },
  { value: "gtu", label: "GTU - Gujarat Technological University" },
];

interface FormErrors {
  discom?: string;
  consumerNumber?: string;
  phoneNumber?: string;
}

interface SwitchToGreenEnergyFormProps {
  onSuccess?: () => void;
}

export function SwitchToGreenEnergyForm({ onSuccess }: SwitchToGreenEnergyFormProps) {
  const [discom, setDiscom] = useState("");
  const [consumerNumber, setConsumerNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [greenEnergyPercentage, setGreenEnergyPercentage] = useState(0);

  const selectedDiscom = DISCOM_OPTIONS.find((d) => d.value === discom);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!discom) {
      newErrors.discom = "Please select a DISCOM company";
    }

    if (!consumerNumber.trim()) {
      newErrors.consumerNumber = "Consumer number is required";
    }

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else {
      const digitsOnly = phoneNumber.replace(/\D/g, "");
      if (!/^[0-9]{10}$/.test(digitsOnly)) {
        newErrors.phoneNumber = "Please enter a valid 10-digit phone number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await authorizeGreenEnergy(discom, consumerNumber);
      window.open(
        response.data.auth_url,
        "discomConsent",
        "width=650,height=750,resizable=yes,scrollbars=yes"
      );
      toast.success("Redirecting to DISCOM consent page...");
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Something went wrong. Please try again.";
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
<div className="bg-white lg:bg-white/70 lg:backdrop-blur-sm rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.06)] border border-gray-100 px-5 py-10 sm:px-6 sm:py-6 max-w-md mx-auto">      
        <div className="flex flex-col items-center mb-8">
        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
          <Leaf className="w-7 h-7 text-emerald-500" strokeWidth={1.5} />
        </div>
        <h1 className="text-2xl sm:text-[26px] font-semibold bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent text-center">
          Switch to Green Energy
        </h1>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* DISCOM Company */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-900">
            DISCOM Company
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`w-full flex items-center justify-between h-12 px-4 text-left rounded-xl border bg-white transition-colors outline-none ${
                errors.discom
                  ? "border-red-400 focus:border-red-500"
                  : isDropdownOpen
                    ? "border-emerald-400 focus:border-emerald-500"
                    : "border-gray-200 hover:border-gray-300 focus:border-emerald-400"
              }`}
            >
              <span
                className={`text-sm ${
                  selectedDiscom ? "text-gray-900" : "text-gray-400"
                }`}
              >
                {selectedDiscom ? selectedDiscom.label : "Select DISCOM Company"}
              </span>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsDropdownOpen(false)}
                />
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                  {DISCOM_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setDiscom(option.value);
                        setIsDropdownOpen(false);
                        if (errors.discom) {
                          setErrors((prev) => ({ ...prev, discom: undefined }));
                        }
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                        discom === option.value
                          ? "bg-emerald-50 text-emerald-700"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {errors.discom && (
            <p className="text-sm text-red-500 mt-1">{errors.discom}</p>
          )}
        </div>

        {/* Consumer Number */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-900  "
          style={{marginTop: "6px"}}>
            Consumer Number
          </label>
          <div
            className={`flex items-center h-12 px-4 rounded-xl border bg-white transition-colors ${
              errors.consumerNumber
                ? "border-red-400"
                : "border-gray-200 hover:border-gray-300 focus-within:border-emerald-400"
            }`}
          >
            <User className="w-4 h-4 text-emerald-500 mr-3 shrink-0" />
            <input
              type="text"
              placeholder="Enter consumer number"
              value={consumerNumber}
              onChange={(e) => {
                setConsumerNumber(e.target.value);
                if (errors.consumerNumber) {
                  setErrors((prev) => ({ ...prev, consumerNumber: undefined }));
                }
              }}
              className="flex-1 h-full text-sm text-gray-900 placeholder:text-gray-400 outline-none bg-transparent"
            />
          </div>
          {errors.consumerNumber && (
            <p className="text-sm text-red-500 mt-1">{errors.consumerNumber}</p>
          )}
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-900"
           style={{marginTop: "6px"}}
          >
            Phone Number
          </label>
          <div
            className={`flex items-center h-12 px-4 rounded-xl border bg-white transition-colors ${
              errors.phoneNumber
                ? "border-red-400"
                : "border-gray-200 hover:border-gray-300 focus-within:border-emerald-400"
            }`}
          >
            <Phone className="w-4 h-4 text-emerald-500 mr-3 shrink-0" />
            <input
              type="tel"
              placeholder="Enter 10-digit phone number"
              value={phoneNumber}
              maxLength={10}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                setPhoneNumber(val);
                if (errors.phoneNumber) {
                  setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
                }
              }}
              className="flex-1 h-full text-sm text-gray-900 placeholder:text-gray-400 outline-none bg-transparent"
            />
          </div>
          {errors.phoneNumber && (
            <p className="text-sm text-red-500 mt-1">{errors.phoneNumber}</p>
          )}
        </div>

        {/* Green Energy Opt-in Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-900">
              Green Energy Opt-in
            </label>
            <span className="text-sm font-semibold text-emerald-600">
              {greenEnergyPercentage}%
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={greenEnergyPercentage}
            onChange={(e) => setGreenEnergyPercentage(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>0%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            {submitError}
          </div>
        )}

        {/* Continue Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-base font-medium rounded-xl hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-200 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Redirecting to DISCOM consent page...
            </>
          ) : (
            <>
              Connect to DISCOM
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </form>

     
      <div className="mt-6 text-center">
       
      </div>
    </div>
  );
}
